import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { getBadges } from '../../api/Badges';
import { createProject, getTags, Tag } from '../../api/Projects';
import { useAppContext } from '../../context/AppContext';
import {
  EU_MC_ASSESSMENT_SUGGESTIONS,
  EU_MC_LANGUAGES,
  EU_MC_PREREQ_SUGGESTIONS,
  newLearningOutcome,
  serializeLearningOutcomes,
} from '../../data/euMcCatalog';
import { LearningOutcome, FinancementType, FINANCEMENT_OPTIONS } from '../../data/mockFormations';
import { BadgeAPI, OrganizationContext } from '../../types';
import { getSelectedOrganizationId } from '../../utils/contextUtils';
import { openProjectSpace } from '../../utils/projectSpaceStore';
import { openFormationSpace, upsertFormation } from '../../utils/formationStore';
import { canCreateFormation } from '../../utils/ofActivationStore';
import { ORGANISME_FORMATION_CERTIFIE_LABEL } from '../../utils/qualityFrameworkLabel';
import {
  getContextFromPageType,
  getTagIdByPathway,
  mapApiProjectToFrontendProject,
  validateImages,
} from '../../utils/projectMapper';
import EuMcSeriesPicker from './EuMcSeriesPicker';
import { getCatalogueFlags } from '../../api/FormationCatalogue';
import { resolveCatalogueOrg } from '../../api/formationCatalogOrg';
import { CatalogueServerFlags } from '../../types/formationCatalogue';
import { getEuMcGateCheckboxLabel } from '../../utils/euMcGateCopy';
import './CreateProjectPage.css';

type CreateKind = 'project' | 'stage' | 'formation';
type ParticipationMode = 'presentiel' | 'distanciel' | 'hybride';
type EqfFramework = 'EQF' | 'QF_EHEA';
type ValidityUnit = 'years' | 'months';

type CarrierOrg = {
  key: string;
  name: string;
  subtitle: string;
  initials: string;
  organizationId?: number;
  qualiopi?: boolean;
  trustLevel?: string | null;
};

const KIND_TO_PROJECT: Record<CreateKind, 'standard' | 'stage' | 'formation'> = {
  project: 'standard',
  stage: 'stage',
  formation: 'formation',
};

const PARTICIPATION_TO_API: Record<ParticipationMode, 'on_site' | 'online' | 'blended'> = {
  presentiel: 'on_site',
  distanciel: 'online',
  hybride: 'blended',
};

function orgInitials(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return 'OR';
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return `${parts[0][0]}${parts[1][0]}`.toUpperCase();
}

function orgSubtitle(org: OrganizationContext, fallback: string): string {
  return org.school_type || org.company_type || fallback;
}

function fromOrg(org: OrganizationContext, fallback: string): CarrierOrg {
  return {
    key: String(org.id),
    name: org.name,
    subtitle: orgSubtitle(org, fallback),
    initials: orgInitials(org.name),
    organizationId: org.id,
    qualiopi: Boolean(org.qualiopi),
    trustLevel: org.trust_level,
  };
}

function parseCreateKind(value: string | null): CreateKind | null {
  if (value === 'project' || value === 'stage' || value === 'formation') return value;
  return null;
}

const CreateProjectPage: React.FC = () => {
  const { state, setCurrentPage, setSelectedProject, addProject } = useAppContext();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const typeFromUrl = parseCreateKind(searchParams.get('type'));

  const [step, setStep] = useState<1 | 2 | 3>(1);
  const [kind, setKind] = useState<CreateKind | null>(typeFromUrl);

  useEffect(() => {
    if (typeFromUrl) setKind(typeFromUrl);
  }, [typeFromUrl]);

  useEffect(() => {
    if (typeFromUrl === 'formation' && !canCreateFormation()) {
      setCurrentPage('of-activation');
      navigate('/of-activation', { replace: true });
    }
  }, [typeFromUrl, navigate, setCurrentPage]);
  const [euMc, setEuMc] = useState(false);
  const [catalogueFlags, setCatalogueFlags] = useState<CatalogueServerFlags | null>(null);
  const [selectedOrgKey, setSelectedOrgKey] = useState<string>('');
  useEffect(() => {
    const org = resolveCatalogueOrg(state.user, state.showingPageType);
    if (!org) {
      setCatalogueFlags({ eu_mc_gate: { allowed: false, reason: 'setting' }, certification_links_enabled: false });
      return;
    }
    getCatalogueFlags(org).then(setCatalogueFlags).catch(() => setCatalogueFlags(null));
  }, [state.user, state.showingPageType]);

  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [imageFile, setImageFile] = useState<File | null>(null);
  const [imagePreview, setImagePreview] = useState('');
  const [pathways, setPathways] = useState<string[]>([]);
  const [learningOutcomes, setLearningOutcomes] = useState('');
  const [euOutcomes, setEuOutcomes] = useState<LearningOutcome[]>([]);
  const [participationMode, setParticipationMode] = useState<ParticipationMode>('presentiel');
  const [workloadHours, setWorkloadHours] = useState('');
  const [workloadEcts, setWorkloadEcts] = useState('');
  const [eqfLevel, setEqfLevel] = useState<number | ''>('');
  const [eqfFramework, setEqfFramework] = useState<EqfFramework>('EQF');
  const [assessmentType, setAssessmentType] = useState('');
  const [teachingLanguages, setTeachingLanguages] = useState<string[]>(['fr']);
  const [langPickerOpen, setLangPickerOpen] = useState(false);
  const [entryRequirements, setEntryRequirements] = useState('');
  const [validityUnlimited, setValidityUnlimited] = useState(true);
  const [validityAmount, setValidityAmount] = useState('2');
  const [validityUnit, setValidityUnit] = useState<ValidityUnit>('years');
  const [seriesPickerOpen, setSeriesPickerOpen] = useState(false);
  const [seriesBadges, setSeriesBadges] = useState<BadgeAPI[]>([]);
  const [financement, setFinancement] = useState<FinancementType | ''>('');
  const [attendanceSurvey, setAttendanceSurvey] = useState(false);

  const [availablePathways, setAvailablePathways] = useState<Tag[]>([]);
  const [pathwaySearch, setPathwaySearch] = useState('');
  const [pathwayOpen, setPathwayOpen] = useState(false);
  const pathwayRef = useRef<HTMLDivElement | null>(null);
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  const carrierOrgs = useMemo<CarrierOrg[]>(() => {
    const contexts = state.user.available_contexts;
    const pageType = state.showingPageType;

    if (pageType === 'pro') {
      const selectedId = getSelectedOrganizationId(state.user, pageType);
      const companies = contexts?.companies || [];
      const current = companies.find((c) => c.id === selectedId) || companies[0];
      return current ? [fromOrg(current, 'Entreprise')] : [];
    }

    if (pageType === 'edu') {
      const selectedId = getSelectedOrganizationId(state.user, pageType);
      const schools = contexts?.schools || [];
      const current = schools.find((s) => s.id === selectedId) || schools[0];
      return current ? [fromOrg(current, 'Établissement scolaire')] : [];
    }

    if (pageType === 'teacher') {
      const schools = contexts?.schools || [];
      const orgs = schools.map((s) => fromOrg(s, 'Établissement scolaire'));
      const independent = contexts?.independent_teacher as
        | { id?: number; organization_name?: string }
        | null
        | undefined;
      if (orgs.length === 0 || independent) {
        const name =
          independent?.organization_name || state.user.name || 'Enseignant indépendant';
        orgs.push({
          key: independent?.id != null ? `independent-${independent.id}` : 'independent',
          name,
          subtitle: 'Enseignant indépendant',
          initials: orgInitials(name),
          organizationId: independent?.id,
        });
      }
      return orgs;
    }

    if (pageType === 'of') {
      const ofOrgs = (contexts as { formation_organizations?: OrganizationContext[] } | undefined)
        ?.formation_organizations || [];
      if (ofOrgs.length) return ofOrgs.map((c) => fromOrg(c, 'Organisme de formation'));
      const catalogueOrg = resolveCatalogueOrg(state.user, state.showingPageType);
      if (catalogueOrg) {
        return [
          {
            key: catalogueOrg.key,
            name: catalogueOrg.name,
            subtitle: catalogueOrg.type === 'School' ? 'Établissement scolaire' : 'Entreprise',
            initials: catalogueOrg.initials,
            organizationId: catalogueOrg.id,
            qualiopi: Boolean(catalogueOrg.qualiopi),
            trustLevel: catalogueOrg.trustLevel,
          },
        ];
      }
      return [];
    }

    return [];
  }, [state.user, state.showingPageType]);

  const selectedOrg = carrierOrgs.find((o) => o.key === selectedOrgKey) || carrierOrgs[0];
  const orgLocked = carrierOrgs.length <= 1;
  // Cartouche = Qualiopi vérifié (ou STRATEGIC_PARTNER qui en découle). Jamais canCreateFormation().
  const showQualiopi = Boolean(
    selectedOrg?.qualiopi || selectedOrg?.trustLevel === 'STRATEGIC_PARTNER'
  );
  const isFormation = kind === 'formation';

  useEffect(() => {
    if (!selectedOrgKey && carrierOrgs[0]) {
      setSelectedOrgKey(carrierOrgs[0].key);
    }
  }, [carrierOrgs, selectedOrgKey]);

  useEffect(() => {
    const load = async () => {
      try {
        const tags = await getTags();
        setAvailablePathways(Array.isArray(tags) ? tags : []);
      } catch {
        setAvailablePathways([]);
      }
    };
    void load();
  }, []);

  useEffect(() => {
    const onDown = (event: MouseEvent) => {
      if (pathwayRef.current && !pathwayRef.current.contains(event.target as Node)) {
        setPathwayOpen(false);
      }
    };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, []);

  const canContinueStep1 = Boolean(kind && selectedOrg);
  const canSubmitForm =
    Boolean(title.trim() && description.trim() && startDate && endDate) &&
    !(startDate && endDate && endDate < startDate);
  const filledEuOutcomes = euOutcomes.filter((o) => o.text.trim());
  const euGoldStarted =
    filledEuOutcomes.length > 0 ||
    (workloadHours.trim() && Number(workloadHours) > 0) ||
    eqfLevel !== '' ||
    Boolean(assessmentType.trim());
  const euGoldCount = euMc
    ? 3 +
      (title.trim() ? 1 : 0) +
      (filledEuOutcomes.length > 0 ? 1 : 0) +
      (euGoldStarted ? 1 : 0) +
      (workloadHours.trim() && Number(workloadHours) > 0 ? 1 : 0) +
      (eqfLevel !== '' ? 1 : 0) +
      (assessmentType.trim() ? 1 : 0)
    : 0;
  const proofEnriched = euMc
    ? filledEuOutcomes.length > 0
    : Boolean(learningOutcomes.trim() && participationMode);

  const kindLabel =
    kind === 'stage' ? 'Stage' : kind === 'formation' ? 'Formation' : 'Projet';

  const goBack = () => {
    if (step === 3) {
      setStep(2);
      setSubmitError(null);
      return;
    }
    if (step === 2) {
      setStep(1);
      setSubmitError(null);
      return;
    }
    if (isFormation) {
      setCurrentPage('formations');
      navigate('/formations');
      return;
    }
    setCurrentPage('projects');
    navigate('/projects');
  };

  const togglePathway = (name: string) => {
    setPathways((prev) => {
      if (prev.includes(name)) return prev.filter((p) => p !== name);
      if (prev.length >= 2) return prev;
      return [...prev, name];
    });
  };

  const handleImage = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;
    const validation = validateImages(file, []);
    if (!validation.valid) {
      setSubmitError(validation.errors.join(', '));
      return;
    }
    setSubmitError(null);
    setImageFile(file);
    const reader = new FileReader();
    reader.onload = () => {
      if (typeof reader.result === 'string') setImagePreview(reader.result);
    };
    reader.readAsDataURL(file);
  };

  const saveProject = async (intent: 'draft' | 'next') => {
    if (!canSubmitForm || !kind || !selectedOrg) {
      setSubmitError('Quatre champs suffisent pour un brouillon : titre, description, date de début, date de fin.');
      return;
    }
    if (endDate < startDate) {
      setSubmitError('La date de fin doit être postérieure à la date de début.');
      return;
    }

    if (kind === 'formation') {
      const pad = (n: number) => String(n).padStart(2, '0');
      const now = new Date();
      const createdLabel = `${pad(now.getDate())}/${pad(now.getMonth() + 1)}/${now.getFullYear()}`;
      const outcomes =
        euMc || euOutcomes.length
          ? euOutcomes.filter((o) => o.text.trim())
          : learningOutcomes
              .split('\n')
              .map((t) => t.trim())
              .filter(Boolean)
              .map((text, i) => ({ id: `lo-${Date.now()}-${i}`, text, kind: 'free' as const }));
      const formation = upsertFormation({
        id: `f-${Date.now()}`,
        title: title.trim(),
        description: description.trim(),
        status: 'draft',
        financement: financement || undefined,
        isEuMcDeclared: euMc,
        startDate,
        endDate,
        attendanceSurveyOptIn: attendanceSurvey,
        durationHours: workloadHours.trim() ? Number(workloadHours) : undefined,
        participationMode,
        learningOutcomes: outcomes,
        imageName: imageFile?.name,
        orgName: selectedOrg?.name,
        workloadEcts: workloadEcts.trim() || undefined,
        eqfLevel: eqfLevel === '' ? undefined : eqfLevel,
        eqfFramework,
        assessmentType: assessmentType.trim() || undefined,
        teachingLanguages,
        entryRequirements: entryRequirements.trim() || undefined,
        meta: `créée le ${createdLabel} · jamais activée · visible par vous seul`,
      });
      if (intent === 'next') {
        openFormationSpace(formation.id, 'gestion');
        setCurrentPage('formation-detail');
        navigate('/formation-detail');
      } else {
        setCurrentPage('formations');
        navigate('/formations');
      }
      return;
    }

    setIsSubmitting(true);
    setSubmitError(null);

    try {
      const tagIds = pathways
        .map((name) => getTagIdByPathway(name, availablePathways))
        .filter((id): id is number => typeof id === 'number');

      const validityMonths = !euMc || validityUnlimited
        ? undefined
        : Number(validityAmount || 0) * (validityUnit === 'years' ? 12 : 1);

      const created = await createProject(
        {
          context: getContextFromPageType(state.showingPageType),
          organization_id: selectedOrg.organizationId,
          project: {
            title: title.trim(),
            description: description.trim(),
            start_date: startDate,
            end_date: endDate,
            status: 'draft',
            private: true,
            tag_ids: euMc ? [] : tagIds,
            project_kind: KIND_TO_PROJECT[kind],
            is_eu_mc_declared: euMc,
            learning_outcomes: euMc
              ? serializeLearningOutcomes(euOutcomes)
              : learningOutcomes.trim() || undefined,
            participation_mode: PARTICIPATION_TO_API[participationMode],
            ...(euMc
              ? {
                  workload_hours: workloadHours.trim() || undefined,
                  workload_ects: workloadEcts.trim() || undefined,
                  project_eqf_level: eqfLevel === '' ? undefined : eqfLevel,
                  project_eqf_framework_type: eqfFramework,
                  assessment_type: assessmentType.trim() || undefined,
                  teaching_languages: teachingLanguages,
                  entry_requirements: entryRequirements.trim() || undefined,
                  validity_period_months: validityMonths && validityMonths > 0 ? validityMonths : null,
                }
              : {}),
          },
        },
        imageFile
      );

      const mapped = mapApiProjectToFrontendProject(created, state.showingPageType, state.user);
      if (!mapped.id && created.id != null) mapped.id = String(created.id);
      addProject(mapped);

      if (intent === 'next') {
        setSelectedProject(mapped);
        openProjectSpace(mapped.id, 'gestion');
        setCurrentPage('project-space');
        navigate('/project-space');
        return;
      }

      setCurrentPage('projects');
      navigate('/projects');
    } catch (error: any) {
      if (error?.response?.data?.details) {
        setSubmitError(
          Array.isArray(error.response.data.details)
            ? error.response.data.details.join(', ')
            : String(error.response.data.details)
        );
      } else if (error?.response?.data?.message) {
        setSubmitError(error.response.data.message);
      } else {
        setSubmitError('Une erreur est survenue lors de l’enregistrement. Veuillez réessayer.');
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const filteredPathways = availablePathways.filter((p) => {
    const name = p.name_fr || p.name || '';
    return !pathwaySearch.trim() || name.toLowerCase().includes(pathwaySearch.toLowerCase());
  });

  return (
    <div className="create-project-page">
      <div className="cp-screen">
        <header className="cp-bar">
          <span>
            {step === 1
              ? 'Créer'
              : step === 3
                ? 'Le module formation'
                : euMc
                  ? 'Le formulaire MC UE'
                  : kindLabel}
          </span>
          <span className="cp-bar-meta">
            {step === 1
              ? 'Que voulez-vous créer ?'
              : `${kindLabel} · ${selectedOrg?.name || ''}`}
          </span>
        </header>

        {step === 1 ? (
          <div className="cp-inner">
            <h1 className="cp-title">Que voulez-vous créer&nbsp;?</h1>

            <section className="cp-blk">
              <h2>
                Organisation porteuse <sup>1</sup>
              </h2>
              {carrierOrgs.length === 0 ? (
                <p className="cp-empty">Aucune organisation porteuse n’est disponible dans cet espace.</p>
              ) : orgLocked && selectedOrg ? (
                <div className="cp-orgchip">
                  <div className={`cp-dot ${showQualiopi ? 'qualiopi' : ''}`}>{selectedOrg.initials}</div>
                  <div>
                    <div className="cp-orgname">{selectedOrg.name}</div>
                    <div className="cp-orgsub">{selectedOrg.subtitle}</div>
                  </div>
                  {showQualiopi && (
                    <span className="cp-cart">{ORGANISME_FORMATION_CERTIFIE_LABEL}</span>
                  )}
                  <div className="cp-lock">définitive</div>
                </div>
              ) : (
                <div className="cp-org-list">
                  {carrierOrgs.map((org) => (
                    <button
                      key={org.key}
                      type="button"
                      className={`cp-orgchip selectable ${selectedOrgKey === org.key ? 'sel' : ''}`}
                      onClick={() => setSelectedOrgKey(org.key)}
                    >
                      <div className="cp-dot">{org.initials}</div>
                      <div>
                        <div className="cp-orgname">{org.name}</div>
                        <div className="cp-orgsub">{org.subtitle}</div>
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </section>

            <section className="cp-blk">
              <h2>
                Le type <sup>2</sup>
              </h2>
              <div className="cp-cards">
                <button
                  type="button"
                  className={`cp-card ${kind === 'project' ? 'sel' : ''}`}
                  onClick={() => setKind('project')}
                >
                  Projet
                  <small>Participants, étapes, compétences attestées</small>
                </button>
                <div className="cp-card off">
                  Stage
                  <small>Période en milieu professionnel</small>
                </div>
                <button
                  type="button"
                  className={`cp-card ${kind === 'formation' ? 'sel' : ''}`}
                  onClick={() => setKind('formation')}
                >
                  Formation
                  <small>Programme, sessions, participants</small>
                </button>
              </div>
            </section>

            <section className="cp-blk">
              <h2>
                Le cadre <sup>3</sup>
              </h2>
              {(() => {
                const gate = catalogueFlags?.eu_mc_gate || { allowed: false, reason: 'setting' as const };
                const label = getEuMcGateCheckboxLabel(gate);
                return (
                  <label className="cp-mc" style={{ display: 'flex', gap: 9, alignItems: 'flex-start' }}>
                    <input
                      type="checkbox"
                      checked={euMc}
                      disabled={label.disabled || kind !== 'formation'}
                      onChange={(e) => setEuMc(e.target.checked)}
                    />
                    <div>
                      <b>Microcertification européenne</b> — {label.body}
                    </div>
                  </label>
                );
              })()}
            </section>

            {euMc && kind === 'formation' && (
              <div className="cp-orbar">
                <div className="cp-orcnt">4/11</div>
                <div className="cp-orlbl">
                  <b>Le compteur européen s’allume.</b> Pays, organisme, assurance qualité et niveau
                  sont déjà renseignés. La fiche complète le cadre.
                </div>
              </div>
            )}

            <div className="cp-warn">
              ⚠
              <div>
                <b>Ces trois choix seront définitifs au premier enregistrement</b> — brouillon compris.
              </div>
            </div>

            <div className="cp-cta">
              <button type="button" className="cp-btn ghost" onClick={goBack}>
                Annuler
              </button>
              <button
                type="button"
                className="cp-btn primary"
                disabled={!canContinueStep1}
                onClick={() => {
                  if (kind === 'formation' && selectedOrg) {
                    sessionStorage.setItem(
                      'kinship.ficheDraft',
                      JSON.stringify({
                        orgKey: selectedOrg.key,
                        orgName: selectedOrg.name,
                        orgInitials: selectedOrg.initials,
                        orgCertified: Boolean(showQualiopi),
                        euMc: euMc && Boolean(catalogueFlags?.eu_mc_gate.allowed),
                      })
                    );
                    setCurrentPage('formation-fiche');
                    navigate('/formation-fiche');
                    return;
                  }
                  setStep(2);
                }}
              >
                Continuer →
              </button>
            </div>
          </div>
        ) : step === 3 && isFormation ? (
          <div className="cp-inner">
            {euMc && (
              <div className="cp-orbar">
                <div className="cp-orcnt">{Math.min(euGoldCount, 9)}/11</div>
                <div className="cp-orlbl">
                  {euGoldCount >= 9 ? (
                    <>
                      <b>Cadre européen complet.</b> Les inscrits de la session porteront le
                      compteur à 10 ; la clôture écrira le onzième.
                    </>
                  ) : (
                    <b>Le cadre européen n’est pas complet.</b>
                  )}
                </div>
              </div>
            )}
            <h1 className="cp-title">Le module formation</h1>
            <p className="cp-sub">Durée, financement, assiduité — ces éléments se figent à la création.</p>

            <div className="cp-gold">
              <label className="cp-field">
                <span>
                  Durée en heures <span className="ob">✱</span>
                  <span className="cp-tag antag">se fige à la création</span>
                  {euMc && <span className="cp-tag">cadre européen</span>}
                </span>
                <input
                  type="number"
                  min={1}
                  value={workloadHours}
                  onChange={(e) => setWorkloadHours(e.target.value)}
                  placeholder="60"
                  style={{ maxWidth: 140 }}
                />
              </label>
            </div>

            <label className="cp-field">
              <span>
                Financement <span className="ob">✱</span>
                <span className="cp-tag antag">se fige à la création</span>
              </span>
              <div className="cp-fin5">
                {FINANCEMENT_OPTIONS.map((opt) => (
                  <button
                    key={opt}
                    type="button"
                    className={`cp-ropt ${financement === opt ? 'sel' : ''}`}
                    onClick={() => setFinancement(opt)}
                  >
                    {opt}
                  </button>
                ))}
              </div>
              <small className="cp-micro">
                Le financeur — l’organisation elle-même — s’identifiera dans Équipe &amp; partenaires,
                dès l’étape suivante (l’espace en mode brouillon).
              </small>
            </label>

            <label className={`cp-opt ${attendanceSurvey ? 'on' : ''}`}>
              <input
                type="checkbox"
                checked={attendanceSurvey}
                onChange={(e) => setAttendanceSurvey(e.target.checked)}
              />
              <div>
                <b>Vérification d’assiduité</b>
                <small>
                  En présentiel, chaque séance s’ouvre par un code de session — la présence de chacun
                  est confirmée et prouvée. Cette option y ajoute un sondage indépendant après les
                  séances. Début et durée : l’assiduité prouvée, face à votre financeur. Une fois
                  engagée, elle ne s’éteint plus.
                </small>
              </div>
            </label>

            {submitError && <div className="cp-error">{submitError}</div>}

            <div className="cp-foot">
              <div className="cp-fcol" style={{ marginRight: 'auto' }}>
                <button type="button" className="cp-btn ghost" onClick={goBack} disabled={isSubmitting}>
                  Annuler
                </button>
              </div>
              <div className="cp-fcol">
                <button
                  type="button"
                  className="cp-btn outline"
                  disabled={!canSubmitForm || isSubmitting}
                  onClick={() => void saveProject('draft')}
                >
                  {isSubmitting ? 'Enregistrement…' : 'Sauvegarder en brouillon'}
                </button>
                <p className="cp-micro">
                  Les co-responsables et vos partenaires pourront y accéder une fois la formation
                  créée.
                </p>
              </div>
              <div className="cp-fcol">
                <button
                  type="button"
                  className="cp-btn primary"
                  disabled={!canSubmitForm || isSubmitting}
                  onClick={() => void saveProject('next')}
                >
                  {isSubmitting ? 'Enregistrement…' : 'Suivant →'}
                </button>
                <p className="cp-micro">L’espace de gestion : préparez tout, puis créez.</p>
              </div>
            </div>
          </div>
        ) : (
          <div className="cp-inner">
            {euMc && (
              <div className="cp-orbar">
                <div className="cp-orcnt">{Math.min(euGoldCount, 9)}/11</div>
                <div className="cp-orlbl">
                  {euGoldCount >= 9 ? (
                    <>
                      <b>Cadre européen complet.</b> Vos participants porteront le compteur à 10 — la
                      clôture écrira le onzième.
                    </>
                  ) : title.trim() ? (
                    <>
                      <b>Cadre européen en cours.</b> Le brouillon est possible dès les 4 champs ✱ —
                      le compteur attendra.
                    </>
                  ) : (
                    <>
                      <b>Le compteur européen s’allume.</b> Pays, organisme et assurance qualité sont
                      déjà garantis — le formulaire conquiert le reste.
                    </>
                  )}
                </div>
              </div>
            )}

            <h1 className="cp-title">{isFormation ? 'Votre formation' : 'Votre projet'}</h1>
            <p className="cp-sub">
              {isFormation
                ? 'Quatre champs suffisent pour un brouillon — la création exigera le cadre complet.'
                : euMc
                  ? 'Quatre champs suffisent pour un brouillon — le cadre européen pourra avancer ensuite.'
                  : 'Quatre champs suffisent pour exister — le reste enrichit votre preuve.'}
            </p>

            <label className="cp-field">
              <span>
                {isFormation ? 'Titre de la formation' : 'Titre du projet'} <span className="ob">✱</span>
                {euMc && <span className="cp-tag">cadre européen — intitulé exact</span>}
              </span>
              <input
                type="text"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder={
                  euMc ? 'Initiation à la médiation numérique' : 'Ateliers radio — Paroles de quartier'
                }
              />
            </label>

            <label className="cp-field">
              <span>
                Description <span className="ob">✱</span>
                {isFormation && <span className="cp-tag antag">se fige à la création</span>}
              </span>
              <textarea
                rows={4}
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder={
                  euMc
                    ? 'Un cycle d’ateliers pour accompagner les publics éloignés du numérique.'
                    : 'Ce que le projet raconte, pour qui, et ce qui s’y fait.'
                }
              />
            </label>

            <div className="cp-two">
              <label className="cp-field">
                <span>
                  Date estimée de début <span className="ob">✱</span>
                  {isFormation && <span className="cp-tag antag">se fige à la création</span>}
                </span>
                <input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} />
              </label>
              <label className="cp-field">
                <span>
                  Date estimée de fin <span className="ob">✱</span>
                  {isFormation && <span className="cp-tag antag">se fige à la création</span>}
                </span>
                <input type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} />
              </label>
            </div>
            {isFormation && (
              <p className="cp-micro" style={{ marginTop: -8, marginBottom: 12 }}>
                Après la création, les dates gardent une porte : le report reste possible jusqu’au
                démarrage — justifié, chaque inscrit notifié.
              </p>
            )}

            {euMc || isFormation ? (
              <>
                <div className="cp-gold">
                  <div className="cp-gold-head">
                    <span>
                      Acquis d’apprentissage <span className="cp-tag">cadre européen</span>
                    </span>
                  </div>
                  <p className="cp-gold-note" style={{ marginTop: 0 }}>
                    Ce que l’apprenant saura faire — une ligne par acquis.
                  </p>
                  <div className="cp-outcomes">
                    {euOutcomes.map((outcome) => (
                      <div key={outcome.id} className="cp-outcome">
                        <span className="cp-drag" aria-hidden>
                          ⠿
                        </span>
                        {outcome.kind === 'series' ? (
                          <span className="cp-outcome-series">{outcome.text}</span>
                        ) : (
                          <input
                            value={outcome.text}
                            placeholder="Ligne libre"
                            onChange={(e) =>
                              setEuOutcomes((prev) =>
                                prev.map((o) =>
                                  o.id === outcome.id ? { ...o, text: e.target.value } : o
                                )
                              )
                            }
                          />
                        )}
                        {outcome.kind === 'free' && (
                          <span className="cp-outcome-kind">(ligne libre)</span>
                        )}
                        <button
                          type="button"
                          className="cp-remove"
                          aria-label="Retirer"
                          onClick={() =>
                            setEuOutcomes((prev) => prev.filter((o) => o.id !== outcome.id))
                          }
                        >
                          ✕
                        </button>
                      </div>
                    ))}
                  </div>
                  <div className="cp-outcome-actions">
                    <button
                      type="button"
                      className="cp-linkbtn"
                      onClick={() => setEuOutcomes((prev) => [...prev, newLearningOutcome('free')])}
                    >
                      + Ajouter un acquis
                    </button>
                    <button
                      type="button"
                      className="cp-linkbtn"
                      onClick={() => {
                        setSeriesPickerOpen((open) => !open);
                        if (!seriesPickerOpen && seriesBadges.length === 0) {
                          void getBadges().then((list) => setSeriesBadges(Array.isArray(list) ? list : []));
                        }
                      }}
                    >
                      📚 Ajouter depuis une série ▾
                    </button>
                  </div>
                  {seriesPickerOpen && (
                    <EuMcSeriesPicker
                      badges={seriesBadges}
                      onCancel={() => setSeriesPickerOpen(false)}
                      onInsert={({ series, referenceLines, skillLines }) => {
                        setEuOutcomes((prev) => [
                          ...prev,
                          ...referenceLines.map((text) => newLearningOutcome('series', text)),
                          ...skillLines.map((text) => newLearningOutcome('free', text)),
                        ]);
                        if (series.eqfLevel && eqfLevel === '') setEqfLevel(series.eqfLevel);
                        setSeriesPickerOpen(false);
                      }}
                    />
                  )}
                </div>

                <div className="cp-gold">
                  <span>
                    Mode de participation <span className="cp-tag">cadre européen</span>
                  </span>
                  <div className="cp-radio">
                    {([
                      ['presentiel', 'Présentiel'],
                      ['distanciel', 'Distanciel'],
                      ['hybride', 'Hybride'],
                    ] as const).map(([id, label]) => (
                      <button
                        key={id}
                        type="button"
                        className={`cp-ropt ${participationMode === id ? 'sel' : ''}`}
                        onClick={() => setParticipationMode(id)}
                      >
                        {label}
                      </button>
                    ))}
                  </div>
                </div>

                <div className="cp-two">
                  <label className="cp-gold cp-field">
                    <span>
                      Durée <span className="cp-tag">cadre européen</span>
                    </span>
                    <div className="cp-hours">
                      <input
                        type="number"
                        min={1}
                        step="0.5"
                        value={workloadHours}
                        onChange={(e) => setWorkloadHours(e.target.value)}
                        placeholder="60"
                      />
                      <span>heures</span>
                    </div>
                  </label>
                  <label className="cp-gold cp-field">
                    <span>
                      Crédits ECTS <em>— si applicable</em>
                    </span>
                    <input
                      type="number"
                      min={0}
                      step="0.5"
                      value={workloadEcts}
                      onChange={(e) => setWorkloadEcts(e.target.value)}
                      placeholder="2"
                    />
                  </label>
                </div>

                <div className="cp-two">
                  <label className="cp-gold cp-field">
                    <span>
                      Niveau EQF <span className="cp-tag">cadre européen</span>
                    </span>
                    <select
                      value={eqfLevel}
                      onChange={(e) => setEqfLevel(e.target.value ? Number(e.target.value) : '')}
                    >
                      <option value="">Choisir…</option>
                      {[1, 2, 3, 4, 5, 6, 7, 8].map((n) => (
                        <option key={n} value={n}>
                          Niveau {n}
                        </option>
                      ))}
                    </select>
                    <p className="cp-gold-note">pré-rempli depuis la série choisie — modifiable</p>
                  </label>
                  <label className="cp-gold cp-field">
                    <span>Type de cadre</span>
                    <select
                      value={eqfFramework}
                      onChange={(e) => setEqfFramework(e.target.value as EqfFramework)}
                    >
                      <option value="EQF">EQF — cadre européen (CEC)</option>
                      <option value="QF_EHEA">QF-EHEA — enseignement supérieur (CC-EEES)</option>
                    </select>
                  </label>
                </div>

                <div className="cp-gold">
                  <label className="cp-field">
                    <span>
                      Type d’évaluation <span className="cp-tag">cadre européen</span>
                    </span>
                    <input
                      value={assessmentType}
                      onChange={(e) => setAssessmentType(e.target.value)}
                      placeholder="Mise en situation pratique évaluée"
                    />
                  </label>
                  <div className="cp-sugs">
                    {EU_MC_ASSESSMENT_SUGGESTIONS.map((sug) => (
                      <button
                        key={sug}
                        type="button"
                        className="cp-sug"
                        onClick={() => setAssessmentType(sug)}
                      >
                        {sug}
                      </button>
                    ))}
                  </div>
                </div>

                <div className="cp-gold">
                  <span>
                    Langue d’enseignement <span className="cp-tag">cadre européen</span>{' '}
                    <em>— hors compteur · plusieurs possibles</em>
                  </span>
                  <div className="cp-langs">
                    {teachingLanguages.map((code) => {
                      const lang = EU_MC_LANGUAGES.find((l) => l.code === code);
                      if (!lang) return null;
                      return (
                        <button
                          key={code}
                          type="button"
                          className="cp-lang"
                          onClick={() =>
                            setTeachingLanguages((prev) => prev.filter((c) => c !== code))
                          }
                        >
                          {lang.flag} {lang.label} ✕
                        </button>
                      );
                    })}
                    <button
                      type="button"
                      className="cp-card-link"
                      onClick={() => setLangPickerOpen((v) => !v)}
                    >
                      + ajouter une langue…
                    </button>
                  </div>
                  {langPickerOpen && (
                    <div className="cp-lang-drop">
                      {EU_MC_LANGUAGES.filter((l) => !teachingLanguages.includes(l.code)).map((lang) => (
                        <button
                          key={lang.code}
                          type="button"
                          onClick={() => {
                            setTeachingLanguages((prev) => [...prev, lang.code]);
                            setLangPickerOpen(false);
                          }}
                        >
                          {lang.flag} {lang.label}
                        </button>
                      ))}
                    </div>
                  )}
                </div>

                <label className="cp-opt">
                  <span>
                    Prérequis <em>— optionnel, champ libre à suggestions</em>
                  </span>
                  <input
                    value={entryRequirements}
                    onChange={(e) => setEntryRequirements(e.target.value)}
                    placeholder="Aucun prérequis"
                  />
                  <div className="cp-sugs">
                    {EU_MC_PREREQ_SUGGESTIONS.map((sug) => (
                      <button
                        key={sug}
                        type="button"
                        className="cp-sug"
                        onClick={() => setEntryRequirements(sug)}
                      >
                        {sug}
                      </button>
                    ))}
                  </div>
                </label>

                <div className="cp-opt">
                  <span>
                    Durée de validité de la compétence <em>— optionnelle</em>
                  </span>
                  <div className="cp-validity">
                    <button
                      type="button"
                      className={`cp-ropt ${validityUnlimited ? 'sel' : ''}`}
                      onClick={() => setValidityUnlimited(true)}
                    >
                      Sans limite
                    </button>
                    <button
                      type="button"
                      className={`cp-ropt ${!validityUnlimited ? 'sel' : ''}`}
                      onClick={() => setValidityUnlimited(false)}
                    >
                      Limitée
                    </button>
                    {!validityUnlimited && (
                      <>
                        <input
                          type="number"
                          min={1}
                          className="cp-validity-n"
                          value={validityAmount}
                          onChange={(e) => setValidityAmount(e.target.value)}
                        />
                        <select
                          value={validityUnit}
                          onChange={(e) => setValidityUnit(e.target.value as ValidityUnit)}
                        >
                          <option value="years">ans</option>
                          <option value="months">mois</option>
                        </select>
                      </>
                    )}
                  </div>
                  <p className="cp-gold-note">
                    telle que fixée par le référentiel officiel de votre certification — jamais de
                    texte libre
                  </p>
                </div>
              </>
            ) : (
              <>
                <div className="cp-field" ref={pathwayRef}>
                  <span>
                    Parcours <em>— choisir parmi les parcours proposés (max 2)</em>
                  </span>
                  {pathways.length > 0 && (
                    <div className="cp-chips">
                      {pathways.map((name) => (
                        <button key={name} type="button" className="cp-chip" onClick={() => togglePathway(name)}>
                          {name} ✓
                        </button>
                      ))}
                    </div>
                  )}
                  <input
                    type="search"
                    value={pathwaySearch}
                    placeholder={pathways.length >= 2 ? 'Maximum 2 parcours sélectionnés' : 'Rechercher…'}
                    disabled={pathways.length >= 2}
                    onChange={(e) => setPathwaySearch(e.target.value)}
                    onFocus={() => pathways.length < 2 && setPathwayOpen(true)}
                  />
                  {pathwayOpen && pathways.length < 2 && (
                    <div className="cp-pathway-drop">
                      {filteredPathways.map((p) => {
                        const name = p.name_fr || p.name;
                        const selected = pathways.includes(name);
                        return (
                          <button
                            key={p.id}
                            type="button"
                            className={selected ? 'sel' : ''}
                            onClick={() => {
                              togglePathway(name);
                              setPathwaySearch('');
                              if (pathways.length >= 1) setPathwayOpen(false);
                            }}
                          >
                            {name}
                          </button>
                        );
                      })}
                      {filteredPathways.length === 0 && <p>Aucun parcours trouvé.</p>}
                    </div>
                  )}
                </div>

                <div className="cp-gold">
                  <label className="cp-field">
                    <span>Acquis d’apprentissage</span>
                    <textarea
                      rows={3}
                      value={learningOutcomes}
                      onChange={(e) => setLearningOutcomes(e.target.value)}
                      placeholder="S’exprimer au micro · préparer et mener une interview · travailler en équipe"
                    />
                  </label>
                  <div className="cp-field">
                    <span>Mode de participation</span>
                    <div className="cp-radio">
                      {([
                        ['presentiel', 'Présentiel'],
                        ['distanciel', 'Distanciel'],
                        ['hybride', 'Hybride'],
                      ] as const).map(([id, label]) => (
                        <button
                          key={id}
                          type="button"
                          className={`cp-ropt ${participationMode === id ? 'sel' : ''}`}
                          onClick={() => setParticipationMode(id)}
                        >
                          {label}
                        </button>
                      ))}
                    </div>
                    <p className="cp-gold-note">
                      {proofEnriched
                        ? <>Acquis + participation remplis → votre preuve passera en <b>Enrichie</b>.</>
                        : 'Acquis + participation remplis → votre preuve passera en Enrichie.'}
                    </p>
                  </div>
                </div>
              </>
            )}

            <div className="cp-field">
              <span>
                Image {isFormation ? 'de la formation' : 'du projet'} <em>— optionnelle (sans elle : titre sur la couleur de confiance)</em>
              </span>
              <div className="cp-imgzone">
                <div
                  className={`cp-imgprev ${imagePreview ? 'has-img' : ''} ${showQualiopi ? 'qualiopi' : ''}`}
                  style={imagePreview ? { backgroundImage: `url(${imagePreview})` } : undefined}
                >
                  {!imagePreview && (title.trim() || 'Titre du projet')}
                </div>
                <div className="cp-imgnote">
                  Sans image, votre affiche porte le titre sur la couleur de votre espace.
                  <button type="button" className="cp-card-link" onClick={() => fileInputRef.current?.click()}>
                    {imagePreview ? 'Changer l’image →' : 'Ajouter une image →'}
                  </button>
                </div>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/jpeg,image/png,image/gif,image/webp,image/svg+xml"
                  hidden
                  onChange={handleImage}
                />
              </div>
            </div>

            {submitError && <div className="cp-error">{submitError}</div>}

            <div className="cp-foot">
              <button type="button" className="cp-btn ghost cp-foot-cancel" onClick={goBack} disabled={isSubmitting}>
                Annuler
              </button>
              <div className="cp-fcol">
                <button
                  type="button"
                  className="cp-btn outline"
                  disabled={!canSubmitForm || isSubmitting}
                  onClick={() => void saveProject('draft')}
                >
                  {isSubmitting ? 'Enregistrement…' : 'Sauvegarder en brouillon'}
                </button>
                <p className="cp-micro">
                  {isFormation
                    ? euMc
                      ? 'Possible dès les 4 champs ✱ — le compteur attendra.'
                      : 'Les co-responsables et vos partenaires pourront y accéder une fois la formation créée.'
                    : euMc
                      ? 'Possible dès les 4 champs ✱ — le compteur attendra.'
                      : 'Les co-responsables et vos partenaires pourront y accéder une fois le projet créé.'}
                </p>
              </div>
              <div className="cp-fcol">
                <button
                  type="button"
                  className="cp-btn primary"
                  disabled={!canSubmitForm || isSubmitting}
                  onClick={() => {
                    if (isFormation) {
                      setStep(3);
                      return;
                    }
                    void saveProject('next');
                  }}
                >
                  {isSubmitting ? 'Enregistrement…' : isFormation ? 'Continuer →' : 'Suivant →'}
                </button>
                <p className="cp-micro">
                  {isFormation
                    ? 'Le module formation : durée, financement, assiduité.'
                    : 'L’espace de gestion : préparez tout, puis créez.'}
                </p>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default CreateProjectPage;
